import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './Tabs.module.scss';
import { useSelector } from 'react-redux';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';

const Tabs = ({ tabs, activeTab, onChange }) => {
  const tablistRef = useRef(null);
  const tabRefs = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const assets = useSelector(state => state.asset);
  const translations = useSelector(state => state.translations);

  const updateIndicator = useCallback(
    () => {
      const activeEl = tabRefs.current[activeTab];
      const container = tablistRef.current;
      if (!activeEl || !container) return;

      const containerRect = container.getBoundingClientRect();
      const tabRect = activeEl.getBoundingClientRect();

      setIndicator({
        left: tabRect.left - containerRect.left + container.scrollLeft,
        width: tabRect.width,
      });
    },
    [activeTab],
  );

  useEffect(
    () => {
      updateIndicator();

      window.addEventListener('resize', updateIndicator);
      return () => window.removeEventListener('resize', updateIndicator);
    },
    [updateIndicator],
  );

  // Auto-scroll active tab into view (mobile carousel)
  useEffect(
    () => {
      const activeEl = tabRefs.current[activeTab];
      if (activeEl) {
        activeEl.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    },
    [activeTab],
  );

  const moveFocus = index => {
    const tab = tabs[index];
    if (!tab) return;
    const el = tabRefs.current[tab.id];
    if (el) {
      el.focus();
    }
  };

  const handleKeyDown = e => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    let handled = true;

    switch (e.key) {
      case 'ArrowRight': {
        const next = (currentIndex + 1) % tabs.length;
        moveFocus(next);
        onChange(tabs[next].id);
        break;
      }
      case 'ArrowLeft': {
        const prev = (currentIndex - 1 + tabs.length) % tabs.length;
        moveFocus(prev);
        onChange(tabs[prev].id);
        break;
      }
      case 'Home':
        moveFocus(0);
        onChange(tabs[0].id);
        break;
      case 'End':
        moveFocus(tabs.length - 1);
        onChange(tabs[tabs.length - 1].id);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onChange(tabs[currentIndex].id);
        break;
      default:
        handled = false;
    }

    if (handled) {
      e.preventDefault();
    }
  };

  const getLabelText = (label="") => {
    const translation = getTranslationEmptyOrNullKey(label, translations)

    return renderMaxLengthText(translation, 22)
  }

  return (
    <div className={styles['wc-tabs']}>
      <div
        ref={tablistRef}
        className={styles['wc-tabs__list']}
        role="tablist"
        aria-label="Navegación principal Mundial 2026"
        onKeyDown={handleKeyDown}
      >
        {tabs.map(tab => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              ref={el => {
                tabRefs.current[tab.id] = el;
              }}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              disabled={tab.disabled}
              className={classNames(styles['wc-tabs__tab'], {
                [styles['wc-tabs__tab--active']]: isActive,
                [styles['wc-tabs__tab--disabled']]: tab.disabled,
              })}
              onClick={() => onChange(tab.id)}
            >
              {tab.icon && (
                <span className={styles['wc-tabs__tab-icon']}>
                  <img
                    src={isActive && tab.iconActive ? assets?.[tab.iconActive] : assets?.[tab.icon]}
                    alt={tab.iconAlt || ''}
                  />
                </span>
              )}
              <span className={styles['wc-tabs__tab-label']}>{getLabelText(tab.label)}</span>
            </button>
          );
        })}

        <span
          className={styles['wc-tabs__indicator']}
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: `${indicator.width}px`,
          }}
        />
      </div>
    </div>
  );
};

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      iconActive: PropTypes.string,
      iconAlt: PropTypes.string,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  activeTab: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default Tabs;
